import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Form from 'reactstrap/lib/Form';
import { isEmpty, isEqual, has, get } from 'lodash-es';

import { EdmundsAPI } from 'client/data/api/api-client';
import { makePromiseCancelable } from 'client/utils/promise';
import { getMetricsHeaders } from 'client/utils/metrics-header';
import { EventToolbox } from 'client/utils/event-toolbox';

import { UnifiedLeadFormCustomConfigPropTypes } from 'site-modules/shared/components/lead-form/lead-form-prop-types/lead-form-prop-types';
import { VALIDATION_URL, VALIDATORS } from 'site-modules/shared/constants/lead-form/validation';
import { formValidation } from 'site-modules/shared/components/form-validation/form-validation';
import { objectToQueryString } from 'site-modules/shared/utils/string';
import { LEAD_FORM_EVENTS } from 'site-modules/shared/constants/lead-form/lead-form-events';

/**
 * Wraps lead form validation and gives access to props and methods.
 */

export class LeadFormValidationWrapperUI extends Component {
  static propTypes = {
    config: PropTypes.shape({
      formType: PropTypes.string.isRequired,
    }).isRequired,
    customConfig: UnifiedLeadFormCustomConfigPropTypes.leadFormConfig.isRequired,
    venomVersion: PropTypes.string,
    pageName: PropTypes.string,
    validationConfig: PropTypes.shape({}),
    leadFormData: PropTypes.shape({}),
    templateComponent: PropTypes.oneOfType([PropTypes.func, PropTypes.object]).isRequired,
    submitLeadForm: PropTypes.func.isRequired,
    showListTargetsLoadingIndicator: PropTypes.bool,
    showSubmitLoadingIndicator: PropTypes.bool,
    isCheckboxProcessed: PropTypes.bool,
    isCheckboxProcessError: PropTypes.bool,
    leadTargets: PropTypes.arrayOf(
      PropTypes.shape({
        inventory: PropTypes.shape({}),
        dealer: PropTypes.shape({}),
        leadAttributes: PropTypes.shape({
          primary: PropTypes.bool.isRequired,
          distance: PropTypes.number,
        }).isRequired,
      })
    ).isRequired,
    submittedLeadTargetsData: PropTypes.shape({
      leadTargets: PropTypes.arrayOf(PropTypes.shape({})),
      trackingData: PropTypes.shape({}),
      leadIds: PropTypes.arrayOf(PropTypes.string),
    }),
    selectedLeadIds: PropTypes.shape({}).isRequired,
    onToggleLeadSelection: PropTypes.func.isRequired,
    onUpdateStyle: PropTypes.func.isRequired,
    fields: PropTypes.shape({}),
    fieldRef: PropTypes.func.isRequired,
    validate: PropTypes.func.isRequired,
    validationErrors: PropTypes.shape({}),
    setFieldsInvalid: PropTypes.func.isRequired,
    year: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    make: PropTypes.string,
    model: PropTypes.string,
    submodel: PropTypes.string,
    trim: PropTypes.string,
    specialIdentifier: PropTypes.string,
    styleId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    zipCode: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    stateCode: PropTypes.string,
    resetFieldValidation: PropTypes.func,
  };

  static defaultProps = {
    venomVersion: '',
    pageName: '',
    customConfig: {},
    validationConfig: {},
    leadFormData: {},
    submittedLeadTargetsData: {},
    showListTargetsLoadingIndicator: false,
    showSubmitLoadingIndicator: false,
    isCheckboxProcessed: false,
    isCheckboxProcessError: false,
    fields: {},
    validationErrors: {},
    year: '',
    make: '',
    model: '',
    submodel: '',
    trim: '',
    specialIdentifier: '',
    styleId: '',
    zipCode: '',
    stateCode: '',
    resetFieldValidation: null,
  };

  constructor(props) {
    super(props);
    this.state = {
      showValidationLoadingIndicator: false,
      shouldFireAttemptSubmit: false,
      validationErrors: {},
    };
    this.wrapper = React.createRef();
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    return {
      shouldFireAttemptSubmit:
        !isEqual(nextProps.validationErrors, prevState.validationErrors) || isEmpty(nextProps.validationErrors),
      validationErrors: nextProps.validationErrors,
    };
  }

  componentDidUpdate(prevProps) {
    if (!isEqual(prevProps.selectedLeadIds, this.props.selectedLeadIds)) {
      // validate card list
      this.props.validate(null, 'selectedLeadsEmpty');
    }
    this.fireAttemptSubmit();
  }

  componentWillUnmount() {
    if (this.promiseCancelable) {
      this.promiseCancelable.cancel();
    }
  }

  /**
   * Sets additional data that can be validated and passed for submission.
   * @param {any} data
   */
  setAdditionalSubmissionData = data => {
    this.additionalSubmissionData = data;
  };

  /**
   * Validation component reference
   *
   * @param field
   * @returns {*}
   */
  componentFieldRef = field => {
    if (!field) {
      return undefined;
    }
    return this.props.fieldRef(field.props.name, field);
  };

  componentWrapperRef = el => {
    this.wrapper = el;
  };

  /**
   * Second validation step
   * Handle errors from server and set fields invalid
   *
   * @param {boolean} emailValid Email correctness
   * @param {boolean} phoneValid Phone correctness
   */
  handleValidationErrors = (emailValid, phoneValid) => {
    const { setFieldsInvalid } = this.props;
    const invalids = [];
    if (!emailValid) invalids.push('email');
    if (!phoneValid) invalids.push('phone');
    setFieldsInvalid(invalids);
  };

  /**
   *  First validation step
   *  Validate submitted info
   * @param {object} event Event object
   */
  preValidateInfo = event => {
    event.preventDefault();
    this.userAttemptSubmit = true;

    // sets skip validation flag that will allow to avoid validation of personal info
    const skipValidation = get(this.additionalSubmissionData, 'skipValidation', false);
    const noLeadTargets = get(this.props.customConfig, 'noLeadTargets', false);

    const isValid =
      skipValidation || (this.props.validate() && (noLeadTargets || !isEmpty(this.props.selectedLeadIds)));
    // any click on the submit button should be tracked
    if (!isValid) {
      // setTimeout used here to ensure scrollToInvalid
      // executes after setState is completed
      setTimeout(this.scrollToInvalid, 0);
      return;
    }
    // collection of FormField
    const form = this.props.fields;
    const firstName = form.firstName.value();
    const lastName = form.lastName.value();
    const phone = form.phone.value();
    const email = form.email.value();
    const isCheckboxChecked = get(form, 'checkbox.input.checked', false);
    const comments = form.comments && form.comments.value();

    const personalInfo = { firstName, lastName, phone, email, comments, isCheckboxChecked };

    if (skipValidation) {
      this.submitLeadForm(personalInfo);
    } else {
      this.postValidateInfo(personalInfo);
    }
  };

  scrollToInvalid = () => {
    const { validationErrors } = this.state;
    const { scrollMap } = this.props.customConfig;
    const form = this.props.fields;
    for (let i = 0; i < scrollMap.length; i += 1) {
      const scrollObj = scrollMap[i];
      if (has(validationErrors, scrollObj.input)) {
        this.scrollTo(`.unified-lead-form ${scrollObj.selector}`);
        if (form && form[scrollObj.input] && typeof form[scrollObj.input].focus === 'function') {
          form[scrollObj.input].focus();
        }
        return;
      }
    }
  };

  // eslint-disable-next-line class-methods-use-this
  scrollTo = selector => {
    if (document) {
      const elem = document.querySelector(selector);
      if (elem) {
        elem.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  };

  toggleValidationIndicator = () => {
    const { showValidationLoadingIndicator } = this.state;
    if (showValidationLoadingIndicator) {
      this.setState({ showValidationLoadingIndicator: false });
    }
  };

  /**
   * Second validation step
   * Validate submitted info and if it's valid call submit function.
   * Statements:
   * - if email and phone valid submits user personal info.
   * - if email or phone invalid call error callback function.
   * @param {Object} personalInfo
   * @returns {Promise}
   */
  postValidateInfo = personalInfo => {
    const {
      config: { formType },
      pageName,
      venomVersion,
    } = this.props;
    const { firstName, lastName, phone, email } = personalInfo;
    const url = `${VALIDATION_URL}${formType}`;
    const requestParams = {
      name: `${firstName} ${lastName}`,
      phone,
      email,
    };
    const params = {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        ...{ headers: getMetricsHeaders('validateSubmitedPersonalInfo', pageName, venomVersion) },
      },
      method: 'POST',
      body: objectToQueryString(requestParams),
    };

    this.setState({ showValidationLoadingIndicator: true });

    this.promiseCancelable = makePromiseCancelable(EdmundsAPI.fetchJson(url, params));
    return this.promiseCancelable.promise
      .then(data => {
        const { emailValid, phoneValid, phoneType, isPrepaidPhone } = data;

        if (emailValid && phoneValid) {
          this.submitLeadForm({ ...personalInfo, phoneType, isPrepaidPhone });
        } else {
          this.setState({ showValidationLoadingIndicator: false });
          this.handleValidationErrors(emailValid, phoneValid);
        }
      })
      .catch(({ isCanceled }) => {
        if (isCanceled) {
          this.toggleValidationIndicator();
          return;
        }
        this.submitLeadForm(personalInfo);
      });
  };

  /**
   * This function is called after 2 step validation: preValidateInfo, postValidateInfo
   * any click on the submit button should be tracked
   * @param personalInfo
   */
  submitLeadForm = personalInfo => {
    this.toggleValidationIndicator();
    this.props.submitLeadForm(personalInfo, this.additionalSubmissionData);
  };

  /**
   * Fire event tracking that user attempt to submit lead form
   * function should call when user click on the submit button
   */
  fireAttemptSubmit = () => {
    // userAttemptSubmit and shouldFireAttemptSubmit flags prevent additional events when user fills input field (fireInputValidationTracking)
    // So, this logic avoids double/triple event tracking on one action
    // any click on the submit button should be tracked with parameter valid = y/n
    if (this.userAttemptSubmit && this.state.shouldFireAttemptSubmit) {
      const { validationErrors, customConfig } = this.props;
      EventToolbox.fireCustomEvent(
        LEAD_FORM_EVENTS.ATTEMPT_SUBMIT,
        {
          valid: isEmpty(validationErrors) ? 'y' : 'n',
          validationErrors,
          total: Object.keys(this.props.selectedLeadIds).length,
          isSimulatedLeadForm: customConfig?.isSimulatedLeadForm,
        },
        this.wrapper
      );
      this.setState({ shouldFireAttemptSubmit: false });
    }
    this.userAttemptSubmit = false;
  };

  validateAndTrack = (...args) => {
    const isValid = this.props.validate(...args);
    // means single field validation, usually called from onBlur input handler
    // no args means validate all fields
    if (args.length) {
      const fieldName = get(args, '[1]');
      EventToolbox.fireCustomEvent(LEAD_FORM_EVENTS.INPUT, { fieldName, isValid }, this.wrapper);
    }
  };

  render() {
    const {
      templateComponent: TemplateComponent,
      leadTargets,
      leadFormData,
      submittedLeadTargetsData,
      showListTargetsLoadingIndicator,
      showSubmitLoadingIndicator,
      validationErrors,
      customConfig,
      make,
      model,
      submodel,
      year,
      trim,
      specialIdentifier,
      styleId,
      zipCode,
      stateCode,
      selectedLeadIds,
      onToggleLeadSelection,
      onUpdateStyle,
      isCheckboxProcessed,
      isCheckboxProcessError,
      resetFieldValidation,
    } = this.props;
    const leadFormId = get(customConfig, 'leadFormId', 'lead-form');

    return (
      <Form
        id={leadFormId || null}
        name="lead-form"
        noValidate
        onSubmit={this.preValidateInfo}
        innerRef={this.componentWrapperRef}
        method="POST"
      >
        <TemplateComponent
          customConfig={customConfig}
          leadTargets={leadTargets}
          leadFormData={leadFormData}
          selectedLeadIds={selectedLeadIds}
          submittedLeadTargetsData={submittedLeadTargetsData}
          onToggleLeadSelection={onToggleLeadSelection}
          onUpdateStyle={onUpdateStyle}
          showListTargetsLoadingIndicator={showListTargetsLoadingIndicator}
          showSubmitLoadingIndicator={showSubmitLoadingIndicator || this.state.showValidationLoadingIndicator}
          fieldRef={this.componentFieldRef}
          setAdditionalSubmissionData={this.setAdditionalSubmissionData}
          validationErrors={validationErrors}
          validate={this.validateAndTrack}
          make={make}
          model={model}
          submodel={submodel}
          year={year}
          trim={trim}
          specialIdentifier={specialIdentifier}
          styleId={styleId}
          zipCode={zipCode}
          stateCode={stateCode}
          isCheckboxProcessed={isCheckboxProcessed}
          isCheckboxProcessError={isCheckboxProcessError}
          resetFieldValidation={resetFieldValidation}
        />
      </Form>
    );
  }
}

export const LeadFormValidationWrapper = formValidation(LeadFormValidationWrapperUI, VALIDATORS);
